// /api/v2/ml-scribe/sessions/{session_id}/backup-audios/
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { getBackupAudio } from '@/pages/api/scribeAPIV2';
import { type ListRecordingsResponse } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { isValid, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { useSessionPermissionCheck } from '../useSessionPermissionCheck';

// Treat Invalid Date as epoch 0 so the comparator can't return NaN —
// Array.sort with a NaN-returning compareFn is implementation-defined
// per ECMA-262 and can produce non-deterministic ordering on malformed
// `created_at` payloads.
const toEpochMs = (iso: string): number => {
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed.getTime() : 0;
};

// paths
export const GET_BACKUP_AUDIO = 'GetBackupAudio';

/** One entry of the recordings list, newest first: an uploaded or a backup audio. */
export type BackupAudioRecording =
  | ListRecordingsResponse['recordings']['uploaded_audios'][number]
  | ListRecordingsResponse['recordings']['backup_audios'][number];

export function useQueryBackupAudio(sessionId: string) {
  const { checkPermission } = useSessionPermissionCheck(sessionId);
  const allowAccessBackupAudio = useCallback(() => {
    return checkPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_VIEW,
      PERMISSION_SUBJECTS.SESSION_VIEW
    );
  }, [checkPermission]);
  return useQuery({
    queryKey: [GET_BACKUP_AUDIO, { sessionId }],
    enabled: !!sessionId && allowAccessBackupAudio(),
    queryFn: async () => {
      const { data } = await getBackupAudio(sessionId);

      return [...data.recordings.uploaded_audios, ...data.recordings.backup_audios].sort(
        (a, b) => toEpochMs(b.created_at) - toEpochMs(a.created_at)
      );
    },
  });
}
